import { Injectable, Injector } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  BpmnProcessActionTypes,
  UpdateProcessInstanceAction
} from '@ispm-client/modules/process-executor/store/actions';
import { Progression, EmptyProgression, Condition } from '@ispm-shared/modules/common';
import * as _ from 'lodash';
import { of } from 'rxjs/observable/of';
import {
    FetchPathwayProgressionAction,
    PathwayActionTypes,
    UpdateProgressionAction
} from '../actions';
import { selectPathwayTestProgression, selectCurationItemId } from '../selectors';
import { ProgressionService } from '@ispm/shared/services/progression.service';
import { selectBpmnDeploymentIdentifier } from '@ispm-client/modules/process-executor/store';
import { SetErrorAction } from '@ispm/shared/store/actions/error.actions';
import { PathwaysConfigurationService } from '../../services';
import { failedToLoadProgressionMessage, failedToSaveMessage } from '../../models/constants';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { ConditionService } from '@ispm/shared/services/condition.service';
import { TUMOR_INFO_DIAGNOSIS } from '../../consts';
import { selectLatestRevisionStatus } from '@ispm/workflow/store/workflow-config.selectors';
import { RevisionStatus } from '@ispm/workflow/constants/revisionStatus.enum';
import { KVP } from '@ispm-shared/modules/common/condition';
import { LogService } from '@ispm/core/services/log.service';
import {ActivatedRoute, ParamMap} from "@angular/router";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

export const PATHWAY_TEST_NAME = 'PATHWAY_TEST';

@Injectable()
export class PathwayTestEffects {

    constructor(private actions$: Actions,
                private store$: Store<any>,
                private injector: Injector,
                private logService : LogService) {
    }

    private getPathwaysConfigurationService(): PathwaysConfigurationService {
        return this.injector.get(PathwaysConfigurationService);
    }

    private getProgressionService(): ProgressionService {
        return this.injector.get(ProgressionService);
    }

    private getConditionService(): ConditionService {
        return this.injector.get(ConditionService);
    }

    private sendLog(effect: string, paramsObj?: any) {
      let logMsg: string = '[PathwayTestEffects] ' + effect;
      Object.keys(paramsObj || {}).forEach((key) => logMsg += ' ' + key + '=' + paramsObj[key]);

      console.log(logMsg);
      this.logService.info(logMsg, '');
    }

    @Effect()
    fetchPathwayTestProgressionEffect$: Observable<Action> = this.actions$.pipe(
        ofType<FetchPathwayProgressionAction>(
            PathwayActionTypes.FETCH_PATHWAY_TEST_PROGRESSION
        ),
        tap((action) => this.sendLog('fetchPathwayTestProgressionEffect$')),
        switchMap(() => this.getProgressionService().search(PATHWAY_TEST_NAME)
            .pipe(
                map((progressions: Array<Progression>) => {
                        let progression: Progression = new EmptyProgression();
                        if (progressions.length > 0) {
                            progression = progressions.length < 2 ? progressions.pop() :
                                progressions.sort((a, b) => (new Date(a.meta.lastUpdated).getTime() - new Date(b.meta.lastUpdated).getTime())).pop();
                        }
                        return progression;
                    }
                ),
                switchMap((progression: Progression) => [
                  new UpdateProgressionAction({ progression})
                ]),
                catchError(error => of(new SetErrorAction({
                    error,
                    devMessage: `fetchPathwayTestProgressionEffect$: Failed to search ${PATHWAY_TEST_NAME} Progression`,
                    userMessage: failedToLoadProgressionMessage
                })))
            )
        )
    );


    @Effect()
    saveProgression: Observable<Action> = this.actions$.pipe(
        ofType<UpdateProcessInstanceAction>(BpmnProcessActionTypes.UPDATE_PROCESS_INSTANCE),
        tap((action) => this.sendLog('saveProgression', {actionType:action.type})),
        switchMap<any, any>((action) => {
            return forkJoin(
                Observable.of(action),
                this.store$.pipe(select(selectPathwayTestProgression),take(1)),
                this.store$.pipe(select(selectBpmnDeploymentIdentifier),take(1)),
                this.getPathwaysConfigurationService().getCurationClinicalKeys().pipe(take(1)),
                this.store$.pipe(select(selectCurationItemId),take(1)),
                this.store$.pipe(select(selectLatestRevisionStatus),take(1)),
                this.getConditionService().searchCurrentPatient().pipe(
                    take(1),
                    map(conditions => conditions.filter(condition => (condition.subResourceType as KVP).code === TUMOR_INFO_DIAGNOSIS)),
                    map(conditions => conditions.length && conditions[conditions.length - 1] || undefined))
            );
        }),
        filter(([
                    action,
                    progression,
                    bpmnDeploymentIdentifier,
                    curationClinicalKeys,
                    curationItemId,
                    latestRevisionStatus,
                    condition
                ]) =>
            !_.isEqual(action.payload.processInstanceId, _.get(progression, 'data.additionalProperties.processInstanceId', 0))
        ),
        switchMap(([
                       action,
                       progression,
                       bpmnDeploymentIdentifier,
                       curationClinicalKeys,
                       curationItemId,
                       latestRevisionStatus,
                       condition
                   ]) => {
                const additionalProperties = {
                    bpmnDeploymentIdentifier: bpmnDeploymentIdentifier,
                    processInstanceId: action.payload.processInstanceId,
                    curationClinicalKeys: curationClinicalKeys,
                    curationItemId: curationItemId,
                    conditionId: undefined
                };
                if (!progression || !progression.id) {
                    additionalProperties.conditionId = condition ? condition.id : undefined;
                    return this.getProgressionService().create(PATHWAY_TEST_NAME, additionalProperties)
                        .pipe(
                            map(progression1 => new UpdateProgressionAction({ progression: progression1})
                            ),
                            catchError(error => of(new SetErrorAction({
                                error,
                                devMessage: `saveProgression: Failed to create ${PATHWAY_TEST_NAME} Progression`,
                                userMessage: failedToSaveMessage
                            })))
                        );
                } else {
                    if (latestRevisionStatus === RevisionStatus.PathwaySelection){
                        additionalProperties.conditionId = condition ? condition.id : undefined;
                        _.set(progression, 'data.additionalProperties', additionalProperties);
                        return this.getProgressionService().update(progression)
                            .pipe(
                                map(progression2 => new UpdateProgressionAction({ progression: progression2})),
                                catchError(error => of(new SetErrorAction({
                                    error,
                                    devMessage: `saveProgression: Failed to create ${PATHWAY_TEST_NAME} Progression`,
                                    userMessage: failedToSaveMessage
                                })))
                            );
                    }  return of(new UpdateProgressionAction({ progression}));
                }
            }
        )
    );


}
